import { AccountProductEnum } from '@varo/graphql-schema-types';

const AMOUNT_PATTERN = /^\d+(?:\.\d{1,2})?$/;
export const EXTERNAL_DEBIT_ACCOUNT_TYPE = 'EXTERNAL_DEBIT';
const DISALLOW_ACCOUNT_INTERACTION_VALUE = 'true';

type TransferPreselectionAccountType =
  | AccountProductEnum.Checking
  | AccountProductEnum.Savings
  | AccountProductEnum.ChargeCard;

type TransferPreselectionDestinationType =
  TransferPreselectionAccountType | typeof EXTERNAL_DEBIT_ACCOUNT_TYPE;

export type TransferPreselectionRouteValue = string | string[];

export type TransferPreselectionRouteParams = {
  fromId?: TransferPreselectionRouteValue;
  fromType?: TransferPreselectionRouteValue;
  toType?: TransferPreselectionRouteValue;
  toId?: TransferPreselectionRouteValue;
  amount?: TransferPreselectionRouteValue;
  targetAmount?: TransferPreselectionRouteValue;
  disallowAccountInteraction?: TransferPreselectionRouteValue;
  typeOfAccount?: TransferPreselectionRouteValue;
  source?: TransferPreselectionRouteValue;
};

export interface TransferPreselectionSource {
  id?: string;
  type?: TransferPreselectionAccountType;
}

interface TransferPreselectionDestination {
  id?: string;
  type?: TransferPreselectionDestinationType;
}

interface TransferPreselectionIntent {
  source?: TransferPreselectionSource;
  destination?: TransferPreselectionDestination;
  /** Desired transfer principal, never the fee-inclusive total debit. */
  amount?: string;
  /** Desired principal, set as close as possible within the route limits. */
  targetAmount?: string;
  isAccountInteractionDisallowed?: boolean;
}

const TRANSFER_ACCOUNT_TYPES = [
  AccountProductEnum.Checking,
  AccountProductEnum.Savings,
  AccountProductEnum.ChargeCard,
] as const;
const TRANSFER_DESTINATION_TYPES = [
  ...TRANSFER_ACCOUNT_TYPES,
  EXTERNAL_DEBIT_ACCOUNT_TYPE,
] as const;

function parseSingleValue(value: TransferPreselectionRouteValue | undefined) {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmedValue = value.trim();
  return trimmedValue.length > 0 ? trimmedValue : undefined;
}

function parseAmount(value: TransferPreselectionRouteValue | undefined) {
  const amount = parseSingleValue(value);
  if (!amount || !AMOUNT_PATTERN.test(amount) || Number(amount) <= 0) {
    return undefined;
  }

  const [wholeAmount, fractionalAmount] = amount.split('.');
  const normalizedWholeAmount = wholeAmount.replace(/^0+(?=\d)/, '');

  return fractionalAmount === undefined
    ? normalizedWholeAmount
    : `${normalizedWholeAmount}.${fractionalAmount}`;
}

function parseSupportedType<T extends string>(
  value: TransferPreselectionRouteValue | undefined,
  supportedTypes: readonly T[],
) {
  const accountType = parseSingleValue(value)?.toUpperCase();
  return supportedTypes.find((supportedType) => supportedType === accountType);
}

function createSelector<T extends string>(
  id: string | undefined,
  type: T | undefined,
) {
  if (!id && !type) {
    return undefined;
  }

  return {
    ...(id ? { id } : {}),
    ...(type ? { type } : {}),
  };
}

export function createTransferPreselectionIntent(
  routeParams: TransferPreselectionRouteParams,
): TransferPreselectionIntent {
  const sourceId = parseSingleValue(routeParams.fromId);
  const sourceType = parseSupportedType(
    routeParams.fromType,
    TRANSFER_ACCOUNT_TYPES,
  );
  const destinationId = parseSingleValue(routeParams.toId);
  const destinationType =
    routeParams.toType === undefined
      ? parseSupportedType(routeParams.typeOfAccount, TRANSFER_ACCOUNT_TYPES)
      : parseSupportedType(routeParams.toType, TRANSFER_DESTINATION_TYPES);
  const targetAmount = parseAmount(routeParams.targetAmount);
  const amount = parseAmount(routeParams.amount);
  const isAccountInteractionDisallowed =
    parseSingleValue(routeParams.disallowAccountInteraction)?.toLowerCase() ===
    DISALLOW_ACCOUNT_INTERACTION_VALUE;
  const hasSourcePreselection =
    routeParams.fromId !== undefined || routeParams.fromType !== undefined;
  const source =
    createSelector(sourceId, sourceType) ??
    (!hasSourcePreselection &&
    routeParams.toType === undefined &&
    destinationType
      ? { type: AccountProductEnum.Checking }
      : undefined);
  const destination = source
    ? createSelector(destinationId, destinationType)
    : undefined;

  return {
    ...(source ? { source } : {}),
    ...(destination ? { destination } : {}),
    ...(amount ? { amount } : {}),
    ...(targetAmount ? { targetAmount } : {}),
    ...(isAccountInteractionDisallowed
      ? { isAccountInteractionDisallowed }
      : {}),
  };
}
